import { Group, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { SourceTypeFilter } from '@/module/Author/Publication/filters/SourceTypeFilter';
import { ExportExcelButton } from '@/module/Export';
import { PublisherTypeFilter } from '@/module/Nomen/PublisherType/PublisherTypeFilter';

import { AddButtonLink, Browse, NumberFilter, useFilter, useNumberFilter, usePagination } from '@/core/components';

import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { ChapterCard } from './components/Card/ChapterCard';
import { getChapterPagedOptions } from './model/ChapterQuery';

export function ChapterBrowse() {
  const { t } = useTranslation();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, ...paginationProps } = usePagination({ search });
  const { id: publisherTypeId, ...typeFilterProps } = useFilter();
  const { id: sourceType, ...sourceTypeFilterProps } = useFilter();
  const { item: publishYear, ...publishYearFilterProps } = useNumberFilter();
  const { item: indexingYear, ...indexingYearFilterProps } = useNumberFilter();

  const { data } = useSuspenseQuery(
    getChapterPagedOptions({
      ...pagination,
      sortDirection,
      search,
      sourceType,
      publisherTypeId,
      publishYear,
      indexingYear,
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={
        <Group>
          <ExportExcelButton entityType="chapters" />
          <AddButtonLink to="search-claimable" />
        </Group>
      }
      title={t('chapters.title')}
      subtitle={t('chapters.subtitle')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <Stack>
            <SearchFilter
              label={t('label.search')}
              description={t('articles.searchBy')}
              {...searchProps}
            />
            <Group>
              <NumberFilter
                label={t('articles.filters.publishYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...publishYearFilterProps}
                flex={0.25}
              />
              <NumberFilter
                label={t('articles.filters.indexingYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...indexingYearFilterProps}
                flex={0.25}
              />
              <PublisherTypeFilter
                label={t('texts.form.fields.publisherType')}
                {...typeFilterProps}
                flex={0.25}
              />
              <SourceTypeFilter
                label={t('publications.filters.sourceType')}
                {...sourceTypeFilterProps}
                flex={0.25}
              />
            </Group>
          </Stack>
        </FilterBox>
      }
      sortDirection={sortDirection}
      toggleSortDirection={toggleSortDirection}
      sortedBy={t('projects.sortedByPublishDate')}
      data={data.list}
      renderItem={chapter => (
        <ChapterCard
          key={chapter.id}
          chapter={chapter}
          to={`${chapter.id}`}
        />
      )}
    />
  );
}
